import React, { createContext, useContext, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

interface GetCoinsContextType {
  isGetCoinsModalOpen: boolean;
  openGetCoinsModal: () => void;
  closeGetCoinsModal: () => void;
}

const GetCoinsContext = createContext<GetCoinsContextType | undefined>(undefined);

export const useGetCoins = () => {
  const context = useContext(GetCoinsContext);
  if (context === undefined) {
    throw new Error('useGetCoins must be used within a GetCoinsProvider');
  }
  return context;
};

/**
 * GetCoinsProvider - Manages Get Coins modal state synced with URL
 * 
 * Modal state is controlled via the `?coins` query parameter:
 * - /home?coins → modal is open
 * - /home → modal is closed
 * 
 * This allows:
 * - Direct linking to the modal state
 * - Browser back button to close the modal
 * - Refresh to preserve modal state
 * 
 * For external links, use /get-coins which renders a full page version.
 */
export const GetCoinsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Modal is open if URL has ?coins query parameter
  // Note: We check for the presence of the key, not its value
  const isGetCoinsModalOpen = searchParams.has('coins');

  const openGetCoinsModal = useCallback(() => {
    // Don't open modal if we're already on the /get-coins page
    if (location.pathname === '/get-coins') {
      return;
    }
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('coins', '');
    // Use replace: false so back button closes the modal
    setSearchParams(newParams, { replace: false });
  }, [searchParams, setSearchParams, location.pathname]);

  const closeGetCoinsModal = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('coins');
    // Use replace: true when closing to avoid polluting history
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const value = {
    isGetCoinsModalOpen,
    openGetCoinsModal,
    closeGetCoinsModal,
  };

  return (
    <GetCoinsContext.Provider value={value}>
      {children}
    </GetCoinsContext.Provider>
  );
};
